import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Loader2 } from 'lucide-react';
import { getTrackingParams } from '@/lib/trackingParams';

const WEBHOOK_URL = 'https://hook.eu1.make.com/l0j9l1ne73h3ooxyf8kf4pkc96kpecvq';
export const THANK_YOU_PATH = '/merci-demande-autopilote';
export const LEAD_STORAGE_KEY = 'autopilote_lead_pending';

const TIME_SLOTS = [
  'Matin (8h – 12h)',
  'Midi (12h – 14h)',
  'Après-midi (14h – 18h)',
  'Soir (après 18h)',
];

function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : '';
}

function getTrackingData() {
  const tracking = getTrackingParams();
  tracking.fbp = readCookie('_fbp');
  tracking.fbc = readCookie('_fbc');
  return tracking;
}

const emptyForm = { name: '', company: '', email: '', phone: '', slotDetails: '' };

export function ContactRequestDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(emptyForm);
  const [slots, setSlots] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const toggleSlot = (slot) => {
    setSlots((prev) => (prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.phone.replace(/\D/g, '').length < 9) {
      setError('Merci d\'indiquer un numéro de téléphone valide.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          company: formData.company.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          preferred_slots: slots,
          preferred_slots_text: slots.join(', '),
          slot_details: formData.slotDetails.trim(),
          source: 'Landing VSL - Présentation Autopilote',
          page_url: window.location.href,
          ...getTrackingData(),
          submitted_at: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`Webhook ${response.status}`);
      }

      // Lu par la page de remerciement pour déclencher le Lead une seule fois
      try {
        sessionStorage.setItem(LEAD_STORAGE_KEY, JSON.stringify({ firstName: formData.name.trim().split(' ')[0] }));
      } catch {
        // stockage indisponible : la page de remerciement s'affiche sans Lead
      }

      setFormData(emptyForm);
      setSlots([]);
      navigate(THANK_YOU_PATH);
    } catch (err) {
      console.error('Erreur envoi demande de contact:', err);
      setError('L\'envoi a échoué. Merci de réessayer dans un instant.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="card-gradient rounded-2xl max-h-[90vh] overflow-y-auto w-[calc(100%-2rem)] sm:max-w-lg p-6 sm:p-8">
        <DialogHeader className="text-left">
          <DialogTitle className="font-display text-2xl font-bold text-foreground">
            Faites votre <span className="gradient-text">demande de contact</span>
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Laissez vos coordonnées : nous vous rappelons pour fixer le rendez-vous et faire votre simulation de rentabilité.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div className="space-y-2">
            <Label htmlFor="vsl-name">Nom et prénom *</Label>
            <Input id="vsl-name" name="name" required autoComplete="name" value={formData.name} onChange={handleChange} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vsl-company">Nom de votre entreprise *</Label>
            <Input id="vsl-company" name="company" required autoComplete="organization" value={formData.company} onChange={handleChange} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vsl-email">Email *</Label>
            <Input
              id="vsl-email"
              name="email"
              type="email"
              required
              autoComplete="email"
              placeholder="vous@entreprise.fr"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vsl-phone">Téléphone pour vous rappeler *</Label>
            <Input
              id="vsl-phone"
              name="phone"
              type="tel"
              required
              autoComplete="tel"
              placeholder="0692 12 34 56"
              value={formData.phone}
              onChange={handleChange}
            />
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium text-foreground mb-3">
              Quand préférez-vous être appelé ? <span className="text-muted-foreground font-normal">(facultatif)</span>
            </legend>
            <div className="grid grid-cols-2 gap-2">
              {TIME_SLOTS.map((slot) => {
                const selected = slots.includes(slot);
                return (
                  <button
                    key={slot}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleSlot(slot)}
                    className={`px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                      selected
                        ? 'border-primary bg-primary/15 text-primary'
                        : 'border-border bg-transparent text-muted-foreground hover:border-primary/50 hover:text-foreground'
                    }`}
                  >
                    {slot}
                  </button>
                );
              })}
            </div>
            <Input
              name="slotDetails"
              aria-label="Précisions sur vos disponibilités"
              placeholder="Précisions (ex : plutôt mardi ou jeudi)"
              value={formData.slotDetails}
              onChange={handleChange}
            />
          </fieldset>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <Button type="submit" variant="hero" size="lg" className="w-full group" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Envoi en cours…
              </>
            ) : (
              <>
                Envoyer ma demande
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </Button>

          <p className="text-xs text-muted-foreground text-center">
            Vos informations servent uniquement à vous recontacter.{' '}
            <a href="/mentions-legales" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-foreground">
              Politique de confidentialité
            </a>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
